import { apiService } from './api';

export interface User {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  isActive: boolean;
  isVerified: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  partnerId?: number;
  inviteCodeUsed?: string;
  partner?: {
    id: number;
    companyName: string;
  };
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  inviteCode?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export const authService = {
  // Authentication
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    console.log('Attempting login with:', { email: credentials.email });
    try {
      const { data } = await apiService.post<AuthResponse>('/auth/login', credentials);
      console.log('Login successful:', data.user.email);
      
      // Store tokens
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      return data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  },

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const { data } = await apiService.post<AuthResponse>('/auth/register', userData);
    
    // Store tokens
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    return data;
  },

  async logout(): Promise<void> {
    try {
      await apiService.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      // Clear impersonation state if present
      localStorage.removeItem('adminToken');
      localStorage.removeItem('isImpersonating');
    }
  },

  async getCurrentUser(): Promise<{ user: User }> {
    const { data } = await apiService.get<{ user: User }>('/auth/me');
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  },

  async updateProfile(updates: Partial<User>): Promise<{ user: User }> {
    const { data } = await apiService.put<{ user: User }>('/auth/profile', updates);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  },

  async changePassword(passwords: ChangePasswordRequest): Promise<{ message: string }> {
    const { data } = await apiService.put<{ message: string }>('/auth/change-password', passwords);
    return data;
  },

  async refreshToken(): Promise<{ token: string; refreshToken: string }> {
    const refreshToken = localStorage.getItem('refreshToken');
    const { data } = await apiService.post<{ token: string; refreshToken: string }>('/auth/refresh-token', {
      refreshToken
    });
    
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('refreshToken', data.refreshToken);
    
    return data;
  },

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const { data } = await apiService.post<{ message: string }>('/auth/forgot-password', { email });
    return data;
  },

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const { data } = await apiService.post<{ message: string }>('/auth/reset-password', { token, newPassword });
    return data;
  },

  // Utility methods
  isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken');
  },

  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  getToken(): string | null {
    return localStorage.getItem('authToken');
  },

  async getUserInviteCodeInfo(): Promise<{
    inviteCode: { code: string; showRepatriationPlan: boolean } | null;
    partner: { id: number; company: string; contactName: string; email: string; phone?: string; logoUrl?: string } | null;
    showRepatriationPlan: boolean;
  }> {
    const { data } = await apiService.get('/users/invite-code-info');
    return data;
  }
};

export default authService;